import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { useEffect, useState } from "react";
import { 
  isRTLLanguage, 
  getTextDirection, 
  setDocumentDirection,
  isArabicLanguage,
  formatNumberForLocale,
  formatDateForLocale,
  getFlexDirection,
  getTextAlign,
  getRTLSafeClass
} from "../util/functions";

export function useLocalization() {
  const instance = useTranslation("translation");
  const [isRTL, setIsRTL] = useState(false);
  const [direction, setDirection] = useState("ltr");

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const rtl = isRTLLanguage(lng);
      const dir = getTextDirection(lng);
      
      setIsRTL(rtl);
      setDirection(dir);
      setDocumentDirection(lng);
      
      // Update body classes for RTL styling
      if (rtl) {
        document.body.classList.add('rtl');
        document.body.classList.remove('ltr');
      } else {
        document.body.classList.add('ltr');
        document.body.classList.remove('rtl');
      }
    };

    // Set initial direction
    handleLanguageChange(i18n.language);

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const changeLanguage = (lng: string) => {
    return i18n.changeLanguage(lng);
  };

  const formatNumber = (number: number | string): string => {
    return formatNumberForLocale(number, i18n.language);
  };

  const formatDate = (date: Date | string): string => {
    return formatDateForLocale(date, i18n.language);
  };

  const getDirectionalClass = (baseClass: string): string => {
    return getRTLSafeClass(baseClass, isRTL);
  };

  // RTL-aware utility methods for layout
  const getRTLLayout = () => ({
    // Flex utilities
    flexRow: isRTL ? 'flex flex-row-reverse' : 'flex flex-row',
    flexRowNormal: 'flex flex-row',
    
    // Text alignment
    textAlign: isRTL ? 'text-right' : 'text-left',
    textAlignOpposite: isRTL ? 'text-left' : 'text-right',
    
    // Margin utilities
    marginLeft: (size: string) => isRTL ? `mr-${size}` : `ml-${size}`,
    marginRight: (size: string) => isRTL ? `ml-${size}` : `mr-${size}`,
    marginStart: (size: string) => isRTL ? `mr-${size}` : `ml-${size}`,
    marginEnd: (size: string) => isRTL ? `ml-${size}` : `mr-${size}`,
    
    // Padding utilities  
    paddingLeft: (size: string) => isRTL ? `pr-${size}` : `pl-${size}`,
    paddingRight: (size: string) => isRTL ? `pl-${size}` : `pr-${size}`,
    paddingStart: (size: string) => isRTL ? `pr-${size}` : `pl-${size}`,
    paddingEnd: (size: string) => isRTL ? `pl-${size}` : `pr-${size}`,
    
    // Float utilities
    floatLeft: isRTL ? 'float-right' : 'float-left',
    floatRight: isRTL ? 'float-left' : 'float-right',
    floatStart: isRTL ? 'float-right' : 'float-left',
    floatEnd: isRTL ? 'float-left' : 'float-right',
    
    // Border utilities
    borderLeft: isRTL ? 'border-r' : 'border-l',
    borderRight: isRTL ? 'border-l' : 'border-r',
    borderStart: isRTL ? 'border-r' : 'border-l',
    borderEnd: isRTL ? 'border-l' : 'border-r',
    
    // Position utilities
    left: isRTL ? 'right' : 'left',
    right: isRTL ? 'left' : 'right',
    start: isRTL ? 'right' : 'left',
    end: isRTL ? 'left' : 'right',
  });

  // Get combined classes for common layouts
  const getLayoutClasses = (layout: 'main' | 'sidebar' | 'full' = 'full') => {
    const rtl = getRTLLayout();
    
    switch (layout) {
      case 'main':
        return `${rtl.flexRow} w-full`;
      case 'sidebar':
        return `${rtl.flexRow} w-full`;
      case 'full':
      default:
        return `flex flex-col w-full ${rtl.textAlign}`;
    }
  };

  return {
    t: instance.t,
    changeLanguage,
    language: i18n.language,
    isRTL,
    direction,
    isArabic: isArabicLanguage(i18n.language),
    formatNumber,
    formatDate,
    getDirectionalClass,
    flexDirection: getFlexDirection(isRTL),
    textAlign: getTextAlign(isRTL),
    getRTLLayout,
    getLayoutClasses,
  };
}
